import { copyFileSync, existsSync, mkdirSync, readFileSync, readdirSync } from "node:fs";
import { homedir } from "node:os";
import { dirname, join, resolve } from "node:path";

import {
  CLAUDE_DIR,
  CODEX_CONFIG_PATH,
  CODEX_DIR,
  ORGX_SESSION_WORK_CONTEXT_DIR,
  ORGX_WIZARD_CONFIG_HOME,
} from "../constants.js";
import { isRecord, parseJsonObject, readTextIfExists, writeJsonFile, writeTextFile } from "./fs.js";
import {
  buildSessionSummaryHookScriptContent,
  HOOK_OUTBOX_MAX_BYTES,
  SESSION_SUMMARY_FIRST_TURN_CONTEXT_MARKER,
  SESSION_SUMMARY_HOOK_MARKER,
} from "./session-summary-hook.js";
import { inspectClientContinuity, type ClientContinuityInspection, type ClientContinuityPaths } from "./client-continuity.js";
import {
  inspectSessionWorkContextActivation,
  sessionWorkContextActivationPath,
  type SessionWorkContextActivationInspection,
} from "./session-work-context.js";

const HOOK_MARKER = "orgx-session-hook.mjs";
// The WEG keystone emitter: a second hook script that derives the execution
// graph from the session transcript and posts it live on Stop. Separate marker
// so it dedupes independently of the session-spool hook.
const EMIT_HOOK_MARKER = "orgx-emit-execution-graph.mjs";
// The lean session-summary hook: bounded per-session counters on the hot path,
// one compact summary post at the end. Its own marker so it dedupes
// independently of the raw spool hook it supersedes.
const SUMMARY_HOOK_MARKER = SESSION_SUMMARY_HOOK_MARKER;
const HOOK_EVENTS = [
  "SessionStart",
  "UserPromptSubmit",
  "PreToolUse",
  "PostToolUse",
  "PermissionRequest",
  "Stop",
  "SessionEnd",
] as const;
const CLAUDE_HOOK_EVENTS = [
  "SessionStart",
  "UserPromptSubmit",
  "PreToolUse",
  "PostToolUse",
  "PostToolUseFailure",
  "PermissionRequest",
  "SubagentStop",
  "Stop",
  "SessionEnd",
] as const;

export type RuntimeHookTarget = "codex" | "claude-code";
export type RuntimeHookWorkEpisodeCapture = "metadata-only" | "bounded";

export interface RuntimeHookPaths {
  claudeSettingsPath: string;
  codexConfigPath: string;
  codexHooksPath: string;
  hookScriptPath: string;
  emitHookScriptPath: string;
  summaryHookScriptPath: string;
  sessionStateDir: string;
  sessionSummaryQueueDir: string;
  deliveryNodePath: string;
  deliveryCliPath: string;
  outboxPath: string;
  workContextPath: string;
}

export interface RuntimeHookInspection {
  paths: RuntimeHookPaths;
  continuity: ClientContinuityInspection;
  workContext: SessionWorkContextActivationInspection;
  installed: {
    claudeCode: boolean;
    codex: boolean;
    hookScript: boolean;
    emitHookScript: boolean;
    summaryHookScript: boolean;
    summaryHookScriptStatus: "current" | "stale" | "missing";
    summaryHookCaptureCapable: boolean;
    summaryHookFirstTurnContextCapable: boolean;
    claudeCodeSummaryHooksInstalled: boolean;
    codexSummaryHooksInstalled: boolean;
    automaticDelivery: boolean;
  };
  codex: {
    configExists: boolean;
    hooksEnabled: boolean;
    hasNotify: boolean;
    notifyPreserved: boolean;
  };
  outboxEvents: number;
  sessionSummaryCaptures: number;
  sessionSummaryQuarantinedCaptures: number;
}

export interface RuntimeHookInstallResult extends RuntimeHookInspection {
  changed: {
    claudeCode: boolean;
    codex: boolean;
    codexConfig: boolean;
    hookScript: boolean;
    emitHookScript: boolean;
    summaryHookScript: boolean;
  };
  backups: string[];
}

export interface RuntimeHookOptions {
  clientContinuityPaths?: Partial<ClientContinuityPaths>;
  claudeSettingsPath?: string;
  codexConfigPath?: string;
  codexHooksPath?: string;
  hookScriptPath?: string;
  emitHookScriptPath?: string;
  summaryHookScriptPath?: string;
  sessionStateDir?: string;
  sessionSummaryQueueDir?: string;
  deliveryNodePath?: string;
  deliveryCliPath?: string;
  now?: Date;
  outboxPath?: string;
  workContextPath?: string;
  cwd?: string;
  workEpisodeCapture?: RuntimeHookWorkEpisodeCapture;
}

function currentPackagedCliPath(): string {
  if (!process.argv[1]) return "";
  const candidate = resolve(process.argv[1]);
  return /\.(?:cts|mts|ts|tsx)$/i.test(candidate) ? "" : candidate;
}

function defaultPaths(options: RuntimeHookOptions = {}): RuntimeHookPaths {
  const hookDir = join(ORGX_WIZARD_CONFIG_HOME, "hooks");
  return {
    claudeSettingsPath: options.claudeSettingsPath ?? join(CLAUDE_DIR, "settings.json"),
    codexConfigPath: options.codexConfigPath ?? CODEX_CONFIG_PATH ?? join(CODEX_DIR, "config.toml"),
    codexHooksPath: options.codexHooksPath ?? join(CODEX_DIR, "hooks.json"),
    hookScriptPath: options.hookScriptPath ?? join(hookDir, HOOK_MARKER),
    emitHookScriptPath: options.emitHookScriptPath ?? join(hookDir, EMIT_HOOK_MARKER),
    summaryHookScriptPath: options.summaryHookScriptPath ?? join(hookDir, SUMMARY_HOOK_MARKER),
    sessionStateDir: options.sessionStateDir ?? join(ORGX_WIZARD_CONFIG_HOME, "sessions"),
    sessionSummaryQueueDir: options.sessionSummaryQueueDir ?? join(ORGX_WIZARD_CONFIG_HOME, "session-summary-captures"),
    deliveryNodePath: options.deliveryNodePath ?? process.execPath,
    deliveryCliPath: options.deliveryCliPath ?? currentPackagedCliPath(),
    outboxPath: options.outboxPath ?? join(hookDir, "events.jsonl"),
    workContextPath: options.workContextPath ?? sessionWorkContextActivationPath(
      options.cwd ?? process.cwd(),
      ORGX_SESSION_WORK_CONTEXT_DIR,
    ),
  };
}

function countJsonlLines(path: string): number {
  const raw = readTextIfExists(path);
  if (!raw) return 0;
  return raw.split(/\r?\n/).filter((line) => line.trim().length > 0).length;
}

function countSessionSummaryCaptures(path: string): number {
  try {
    return readdirSync(path).filter((name) => name.endsWith(".capture.json")).length;
  } catch {
    return 0;
  }
}

function countSessionSummaryQuarantinedCaptures(path: string): number {
  try {
    return readdirSync(join(path, "quarantine")).filter((name) =>
      name.includes(".malformed")
    ).length;
  } catch {
    return 0;
  }
}

function backupPath(path: string, now: Date): string {
  const timestamp = now.toISOString().replace(/[:.]/g, "-");
  return `${path}.bak.${timestamp}`;
}

function backupExisting(path: string, now: Date): string | null {
  if (!existsSync(path)) return null;
  const backup = backupPath(path, now);
  copyFileSync(path, backup);
  return backup;
}

function hasOrgxHook(raw: string | null): boolean {
  return Boolean(raw?.includes(HOOK_MARKER));
}

function hasSummaryHookCommand(
  raw: string | null,
  event: "SessionStart" | "SessionEnd",
  sourceClient: RuntimeHookTarget,
): boolean {
  return Boolean(
    raw?.includes(SUMMARY_HOOK_MARKER) &&
      raw.includes(`--event=${event}`) &&
      raw.includes(`--source_client=${sourceClient}`),
  );
}

function hasNativeSummaryHooks(
  raw: string | null,
  sourceClient: RuntimeHookTarget,
): boolean {
  return (
    hasSummaryHookCommand(raw, "SessionStart", sourceClient) &&
    hasSummaryHookCommand(raw, "SessionEnd", sourceClient)
  );
}

function codexHooksEnabled(raw: string | null): boolean {
  return Boolean(raw && /^\s*hooks\s*=\s*true\s*$/m.test(raw));
}

function codexHasNotify(raw: string | null): boolean {
  return Boolean(raw && /^\s*notify\s*=/m.test(raw));
}

/**
 * The legacy raw-event spool hook.
 *
 * It stays installed because `hooks doctor` and `hooks replay` read its
 * `events.jsonl`, but it is now BOUNDED: at ORGX_WIZARD_HOOK_OUTBOX_MAX_BYTES
 * (default 8MB) the file rotates to a single `.1` generation, so total spool
 * footprint is capped at 2x instead of growing forever the way it grew to
 * 148MB. Replay only ever consumes the recent tail, so nothing it uses is lost.
 * Set ORGX_WIZARD_HOOK_SPOOL=off to disable raw spooling entirely; the lean
 * session-summary hook covers the durable history path either way.
 */
export function buildRuntimeHookScriptContent(): string {
  return `#!/usr/bin/env node
import { appendFileSync, mkdirSync, renameSync, statSync } from "node:fs";
import { dirname, join } from "node:path";
import { homedir } from "node:os";

const MAX_OUTBOX_BYTES = ${HOOK_OUTBOX_MAX_BYTES};

function outboxLimit() {
  const raw = parseInt(process.env.ORGX_WIZARD_HOOK_OUTBOX_MAX_BYTES || "", 10);
  return Number.isFinite(raw) && raw > 0 ? raw : MAX_OUTBOX_BYTES;
}

function spoolDisabled() {
  return String(process.env.ORGX_WIZARD_HOOK_SPOOL || "").trim().toLowerCase() === "off";
}

function rotateIfOversized(path) {
  try {
    if (statSync(path).size > outboxLimit()) renameSync(path, path + ".1");
  } catch (error) {
    // No spool yet, or rotation is not possible. Either way, keep going.
  }
}

function parseArgs(argv) {
  const args = {};
  for (const arg of argv) {
    if (!arg.startsWith("--")) continue;
    const [key, ...rest] = arg.slice(2).split("=");
    args[key] = rest.length > 0 ? rest.join("=") : "true";
  }
  return args;
}

function pickString(...values) {
  for (const value of values) {
    if (typeof value !== "string") continue;
    const trimmed = value.trim();
    if (trimmed) return trimmed;
  }
  return undefined;
}

async function readStdin() {
  const chunks = [];
  for await (const chunk of process.stdin) chunks.push(Buffer.from(chunk));
  return Buffer.concat(chunks).toString("utf8");
}

function parseJson(value) {
  try {
    const parsed = JSON.parse(value || "{}");
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

function summarize(payload) {
  const toolName = pickString(payload.tool_name, payload.toolName, payload.tool?.name, payload.name);
  const prompt = pickString(payload.prompt);
  return {
    tool_name: toolName,
    prompt_chars: prompt ? prompt.length : undefined,
    payload_keys: Object.keys(payload).slice(0, 40),
  };
}

const args = parseArgs(process.argv.slice(2));
const raw = await readStdin();
const payload = parseJson(raw);
const outbox = pickString(
  process.env.ORGX_WIZARD_HOOK_OUTBOX,
  args.outbox,
  join(homedir(), ".config", "useorgx", "wizard", "hooks", "events.jsonl")
);
const event = pickString(args.event, payload.hook_event_name, payload.hookEventName, payload.event, payload.eventName, "unknown");
const sourceClient = pickString(args.source_client, args["source-client"], "unknown");

const record = {
  schema_version: "2026-05-07",
  source: "orgx_wizard_runtime_hook",
  source_client: sourceClient,
  event,
  session_id: pickString(payload.session_id, payload.sessionId, payload.conversation_id, payload.conversationId),
  turn_id: pickString(payload.turn_id, payload.turnId),
  cwd: pickString(payload.cwd, payload.working_directory, payload.workspace, process.cwd()),
  transcript_path: pickString(payload.transcript_path, payload.transcriptPath),
  timestamp: new Date().toISOString(),
  summary: summarize(payload),
};

try {
  if (!spoolDisabled()) {
    mkdirSync(dirname(outbox), { recursive: true, mode: 0o700 });
    rotateIfOversized(outbox);
    appendFileSync(outbox, JSON.stringify(record) + "\\n", { encoding: "utf8", mode: 0o600 });
  }
} catch {
  // Hooks must never break the user's agent runtime.
}

process.exit(0);
`;
}

function buildHookCommand(params: {
  event: string;
  hookScriptPath: string;
  outboxPath: string;
  sourceClient: RuntimeHookTarget;
}): string {
  return [
    "node",
    JSON.stringify(params.hookScriptPath),
    `--event=${params.event}`,
    `--source_client=${params.sourceClient}`,
    `--outbox=${params.outboxPath}`,
  ].join(" ");
}

/**
 * The WEG keystone emitter installed alongside the session-spool hook. On Stop
 * it derives the execution graph from the session transcript (one step node per
 * real tool call; failures marked failed; NO depends_on edges) and posts it to
 * /api/v1/live/execution-graph. This legacy compatibility path is explicitly
 * opt-in with ORGX_EMIT_EXECUTION_GRAPH; the default capture path is the
 * compact session-summary hook. That prevents routine hook installation from
 * rereading complete transcripts or issuing an additional direct Stop-time
 * network request.
 *
 * The generated script intentionally avoids backslash escapes (newline-split via
 * String.fromCharCode(10), trailing-slash trim via endsWith) so it survives this
 * template literal verbatim.
 */
export function buildExecutionGraphEmitScriptContent(): string {
  return `#!/usr/bin/env node
import { readFileSync } from "node:fs";

function parseArgs(argv) {
  const args = {};
  for (const arg of argv) {
    if (!arg.startsWith("--")) continue;
    const i = arg.indexOf("=");
    if (i < 0) args[arg.slice(2)] = "true";
    else args[arg.slice(2, i)] = arg.slice(i + 1);
  }
  return args;
}
function truthy(v) {
  return typeof v === "string" && ["1", "true", "yes", "on"].includes(v.toLowerCase());
}
function pick() {
  for (let i = 0; i < arguments.length; i++) {
    const v = arguments[i];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}
function clamp(s, m) {
  return typeof s === "string" ? s.slice(0, m) : undefined;
}
async function readStdin() {
  try {
    const c = [];
    for await (const ch of process.stdin) c.push(Buffer.from(ch));
    return Buffer.concat(c).toString("utf8");
  } catch (e) {
    return "";
  }
}
function jsonl(raw) {
  const out = [];
  if (typeof raw !== "string") return out;
  for (const line of raw.split(String.fromCharCode(10))) {
    const t = line.trim();
    if (!t) continue;
    try { out.push(JSON.parse(t)); } catch (e) {}
  }
  return out;
}
function blocks(entry) {
  const m = entry && (entry.message || entry);
  const c = m && m.content;
  return Array.isArray(c) ? c : [];
}
function derive(entries, max) {
  const errs = new Map();
  for (const e of entries) for (const b of blocks(e)) {
    if (b && b.type === "tool_result" && b.tool_use_id) errs.set(b.tool_use_id, !!b.is_error);
  }
  const steps = [];
  for (const e of entries) for (const b of blocks(e)) {
    if (b && b.type === "tool_use") steps.push({ id: b.id, name: typeof b.name === "string" ? b.name : "tool" });
  }
  const nodes = [{ id: "session", type: "task", title: "Claude Code session", status: "completed", requires_evidence: false }];
  const capped = steps.slice(-(max - 1));
  for (let i = 0; i < capped.length; i++) {
    const s = capped[i];
    nodes.push({ id: "step-" + (i + 1), type: "step", title: clamp(s.name, 500), status: errs.get(s.id) === true ? "failed" : "completed", requires_evidence: false });
  }
  return nodes;
}
function authHeaders(env) {
  if (env.ORGX_CLIENT_KEY) return { Authorization: "Bearer " + env.ORGX_CLIENT_KEY };
  if (env.ORGX_API_KEY) {
    const h = { Authorization: "Bearer " + env.ORGX_API_KEY };
    if (env.ORGX_USER_ID) h["X-Orgx-User-Id"] = env.ORGX_USER_ID;
    return h;
  }
  if (env.ORGX_SERVICE_KEY && env.ORGX_USER_ID) return { Authorization: "Bearer " + env.ORGX_SERVICE_KEY, "X-Orgx-User-Id": env.ORGX_USER_ID };
  return null;
}
(async () => {
  try {
    const args = parseArgs(process.argv.slice(2));
    const env = process.env;
    if (!truthy(env.ORGX_EMIT_EXECUTION_GRAPH)) return;
    const initiative = pick(env.ORGX_INITIATIVE_ID, args.initiative);
    if (!initiative) return;
    const auth = authHeaders(env);
    if (!auth) return;
    const raw = await readStdin();
    let hook = {};
    try { hook = raw && raw.trim() ? JSON.parse(raw) : {}; } catch (e) { hook = {}; }
    const tp = pick(env.ORGX_TRANSCRIPT_PATH, hook.transcript_path);
    let entries = [];
    if (tp) { try { entries = jsonl(readFileSync(tp, "utf8")); } catch (e) { entries = []; } }
    let max = parseInt(env.ORGX_EMIT_MAX_NODES || "", 10);
    if (!Number.isFinite(max)) max = 40;
    const nodes = derive(entries, max);
    const sc = pick(args.source_client, env.ORGX_SOURCE_CLIENT, "claude-code");
    const event = {
      schema_version: "1.0.0",
      initiative_id: initiative,
      source_client: sc,
      summary: clamp(env.ORGX_EMIT_SUMMARY, 2000) || (sc + " session: " + (nodes.length - 1) + " step(s)"),
      nodes: nodes,
      edges: [],
      trust_events: [],
      metadata: { emitter: "orgx-wizard-runtime-hook", via: "stop-hook" },
    };
    if (env.ORGX_RUN_ID) event.run_id = env.ORGX_RUN_ID;
    else event.correlation_id = clamp(pick(hook.session_id, env.ORGX_CORRELATION_ID) || (sc + "-" + initiative), 120);
    let base = env.ORGX_BASE_URL || "https://useorgx.com";
    while (base.endsWith("/")) base = base.slice(0, -1);
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), parseInt(env.ORGX_EMIT_TIMEOUT_MS || "", 10) || 4000);
    try {
      await fetch(base + "/api/v1/live/execution-graph", {
        method: "POST",
        headers: Object.assign({ "Content-Type": "application/json" }, auth),
        body: JSON.stringify(event),
        signal: ctrl.signal,
      });
    } catch (e) {} finally { clearTimeout(timer); }
  } catch (e) {}
  process.exit(0);
})();
`;
}

function buildEmitHookCommand(params: {
  emitHookScriptPath: string;
  sourceClient: RuntimeHookTarget;
}): string {
  return [
    "node",
    JSON.stringify(params.emitHookScriptPath),
    `--source_client=${params.sourceClient}`,
  ].join(" ");
}

function buildSummaryHookCommand(params: {
  deliveryCliPath: string;
  deliveryNodePath: string;
  event: string;
  sessionStateDir: string;
  sessionSummaryQueueDir: string;
  sourceClient: RuntimeHookTarget;
  summaryHookScriptPath: string;
  workEpisodeCapture?: RuntimeHookWorkEpisodeCapture;
}): string {
  const terminal = params.event === "Stop" || params.event === "SessionEnd";
  return [
    "node",
    JSON.stringify(params.summaryHookScriptPath),
    `--event=${params.event}`,
    `--source_client=${params.sourceClient}`,
    `--state_dir=${JSON.stringify(params.sessionStateDir)}`,
    `--queue_dir=${JSON.stringify(params.sessionSummaryQueueDir)}`,
    ...(params.workEpisodeCapture === "bounded"
      ? ["--work_episode_capture=bounded"]
      : []),
    ...(terminal && params.deliveryNodePath && params.deliveryCliPath
      ? [
          `--delivery_node=${JSON.stringify(params.deliveryNodePath)}`,
          `--delivery_cli=${JSON.stringify(params.deliveryCliPath)}`,
        ]
      : []),
  ].join(" ");
}

/**
 * Put exactly one summary hook on Claude's universal matcher for this event.
 * Older Wizard releases placed it beside the selective raw-spool hook, which
 * silently missed read-only and newer tools. Migrate only the Wizard summary
 * command; preserve every user hook and the bounded legacy spool rule.
 */
function ensureClaudeSummaryHook(
  rules: Array<Record<string, unknown>>,
  event: string,
  paths: RuntimeHookPaths,
  workEpisodeCapture?: RuntimeHookWorkEpisodeCapture,
): boolean {
  let changed = false;
  let universal = rules.find((entry) => isRecord(entry) && entry.matcher === "");
  if (!universal) {
    universal = { matcher: "", hooks: [] };
    rules.push(universal);
    changed = true;
  }
  const desiredCommand = buildSummaryHookCommand({
    deliveryCliPath: paths.deliveryCliPath,
    deliveryNodePath: paths.deliveryNodePath,
    event,
    sessionStateDir: paths.sessionStateDir,
    sessionSummaryQueueDir: paths.sessionSummaryQueueDir,
    sourceClient: "claude-code",
    summaryHookScriptPath: paths.summaryHookScriptPath,
    ...(workEpisodeCapture ? { workEpisodeCapture } : {}),
  });
  let desiredKept = false;

  for (const rule of rules) {
    if (!isRecord(rule)) continue;
    const hooks = Array.isArray(rule.hooks)
      ? (rule.hooks as Array<Record<string, unknown>>)
      : [];
    const next: Array<Record<string, unknown>> = [];
    for (const hook of hooks) {
      const isSummary =
        isRecord(hook) &&
        typeof hook.command === "string" &&
        hook.command.includes(SUMMARY_HOOK_MARKER);
      if (!isSummary) {
        next.push(hook);
        continue;
      }
      if (
        rule === universal &&
        hook.type === "command" &&
        hook.command === desiredCommand &&
        !desiredKept
      ) {
        next.push(hook);
        desiredKept = true;
      } else {
        changed = true;
      }
    }
    if (next.length !== hooks.length) rule.hooks = next;
  }

  if (!desiredKept) {
    const hooks = Array.isArray(universal.hooks)
      ? (universal.hooks as Array<Record<string, unknown>>)
      : [];
    hooks.push({ type: "command", command: desiredCommand });
    universal.hooks = hooks;
    changed = true;
  }
  return changed;
}

function mergeCodexHooks(
  raw: string | null,
  paths: RuntimeHookPaths,
  workEpisodeCapture?: RuntimeHookWorkEpisodeCapture,
): { changed: boolean; value: Record<string, unknown> } {
  const value = parseJsonObject(raw);
  const hooks = isRecord(value.hooks) ? value.hooks : {};
  let changed = false;

  for (const event of HOOK_EVENTS) {
    const existing = Array.isArray(hooks[event])
      ? (hooks[event] as Array<unknown>)
      : [];
    const command = buildHookCommand({
      event,
      hookScriptPath: paths.hookScriptPath,
      outboxPath: paths.outboxPath,
      sourceClient: "codex",
    });
    const desiredSummaryCommand = buildSummaryHookCommand({
      deliveryCliPath: paths.deliveryCliPath,
      deliveryNodePath: paths.deliveryNodePath,
      event,
      sessionStateDir: paths.sessionStateDir,
      sessionSummaryQueueDir: paths.sessionSummaryQueueDir,
      sourceClient: "codex",
      summaryHookScriptPath: paths.summaryHookScriptPath,
      ...(workEpisodeCapture ? { workEpisodeCapture } : {}),
    });

    // Current Codex hook configuration uses matcher groups containing typed
    // command handlers. Wizard <=0.1.61 wrote legacy bare `{ command }`
    // entries; Codex parses the file but does not execute those entries. Move
    // only Wizard-owned handlers into one universal canonical group and leave
    // every unrelated user or plugin group untouched.
    let universal = existing.find(
      (entry) =>
        isRecord(entry) &&
        Array.isArray(entry.hooks) &&
        (entry.matcher === undefined || entry.matcher === ""),
    );
    if (!isRecord(universal)) {
      universal = { matcher: "", hooks: [] };
      existing.push(universal);
      changed = true;
    }
    const universalGroup = universal as Record<string, unknown>;

    const desired = [
      { marker: HOOK_MARKER, command },
      { marker: SUMMARY_HOOK_MARKER, command: desiredSummaryCommand },
    ];
    const kept = new Set<string>();
    const normalized: Array<unknown> = [];

    for (const entry of existing) {
      if (!isRecord(entry)) {
        normalized.push(entry);
        continue;
      }

      const legacyCommand = entry.command;
      if (typeof legacyCommand === "string") {
        if (desired.some(({ marker }) => legacyCommand.includes(marker))) {
          changed = true;
          continue;
        }
        normalized.push(entry);
        continue;
      }

      if (!Array.isArray(entry.hooks)) {
        normalized.push(entry);
        continue;
      }

      const nextHandlers: Array<unknown> = [];
      for (const handler of entry.hooks) {
        if (!isRecord(handler)) {
          nextHandlers.push(handler);
          continue;
        }
        const handlerCommand = handler.command;
        if (typeof handlerCommand !== "string") {
          nextHandlers.push(handler);
          continue;
        }
        const target = desired.find(({ marker }) =>
          handlerCommand.includes(marker),
        );
        if (!target) {
          nextHandlers.push(handler);
          continue;
        }
        if (
          entry === universalGroup &&
          handler.type === "command" &&
          handlerCommand === target.command &&
          !kept.has(target.marker)
        ) {
          nextHandlers.push(handler);
          kept.add(target.marker);
        } else {
          changed = true;
        }
      }
      entry.hooks = nextHandlers;
      normalized.push(entry);
    }

    const universalHandlers = universalGroup.hooks as Array<unknown>;
    for (const target of desired) {
      if (kept.has(target.marker)) continue;
      universalHandlers.push({ type: "command", command: target.command });
      changed = true;
    }
    hooks[event] = normalized;
  }

  value.hooks = hooks;
  return { changed: changed || !raw, value };
}

function mergeClaudeHooks(
  raw: string | null,
  paths: RuntimeHookPaths,
  workEpisodeCapture?: RuntimeHookWorkEpisodeCapture,
): { changed: boolean; value: Record<string, unknown> } {
  const value = parseJsonObject(raw);
  const hooksRoot = isRecord(value.hooks) ? value.hooks : {};
  let changed = false;

  for (const event of CLAUDE_HOOK_EVENTS) {
    const list = Array.isArray(hooksRoot[event]) ? hooksRoot[event] as Array<Record<string, unknown>> : [];
    const matcher =
      event === "PreToolUse" ||
      event === "PostToolUse" ||
      event === "PostToolUseFailure"
        ? "Bash|Write|Edit|MultiEdit|mcp__.*"
        : "";
    const command = buildHookCommand({
      event,
      hookScriptPath: paths.hookScriptPath,
      outboxPath: paths.outboxPath,
      sourceClient: "claude-code",
    });
    let rule = list.find((entry) => isRecord(entry) && entry.matcher === matcher);
    if (!rule) {
      rule = { matcher, hooks: [] };
      list.push(rule);
      changed = true;
    }

    let hooks = Array.isArray(rule.hooks)
      ? (rule.hooks as Array<Record<string, unknown>>)
      : [];
    const already = hooks.some((entry) =>
      isRecord(entry) &&
      entry.type === "command" &&
      typeof entry.command === "string" &&
      entry.command.includes(HOOK_MARKER)
    );
    if (!already) {
      hooks.push({ type: "command", command });
      rule.hooks = hooks;
      changed = true;
    }

    // The lean session-summary hook runs on the universal matcher for every
    // registered event. This captures supported read-only and newer tools too,
    // while the legacy raw spool remains on its narrower matcher.
    if (ensureClaudeSummaryHook(list, event, paths, workEpisodeCapture)) {
      changed = true;
    }
    hooks = Array.isArray(rule.hooks)
      ? (rule.hooks as Array<Record<string, unknown>>)
      : [];

    // The WEG keystone live-emit runs on Stop only (one execution-graph post per
    // turn boundary). Registered with its own marker so it dedupes separately.
    if (event === "Stop") {
      const emitCommand = buildEmitHookCommand({
        emitHookScriptPath: paths.emitHookScriptPath,
        sourceClient: "claude-code",
      });
      const emitAlready = hooks.some((entry) =>
        isRecord(entry) &&
        entry.type === "command" &&
        typeof entry.command === "string" &&
        entry.command.includes(EMIT_HOOK_MARKER)
      );
      if (!emitAlready) {
        hooks.push({ type: "command", command: emitCommand });
        rule.hooks = hooks;
        changed = true;
      }
    }
    hooksRoot[event] = list;
  }

  value.hooks = hooksRoot;
  return { changed: changed || !raw, value };
}

function ensureCodexHooksFeature(raw: string | null): { changed: boolean; value: string } {
  const current = raw ?? "";
  const withoutDeprecatedFlag = current.replace(
    /^\s*codex_hooks\s*=\s*(?:true|false)\s*(?:\r?\n|$)/gm,
    "",
  );
  const removedDeprecatedFlag = withoutDeprecatedFlag !== current;

  if (codexHooksEnabled(withoutDeprecatedFlag)) {
    return {
      changed: removedDeprecatedFlag,
      value: withoutDeprecatedFlag,
    };
  }

  if (/^\s*hooks\s*=\s*false\s*$/m.test(withoutDeprecatedFlag)) {
    return {
      changed: true,
      value: withoutDeprecatedFlag.replace(
        /^\s*hooks\s*=\s*false\s*$/m,
        "hooks = true",
      ),
    };
  }

  if (/^\s*\[features\]\s*$/m.test(withoutDeprecatedFlag)) {
    return {
      changed: true,
      value: withoutDeprecatedFlag.replace(
        /^(\s*\[features\]\s*)$/m,
        "$1\nhooks = true",
      ),
    };
  }

  const suffix = withoutDeprecatedFlag.trimEnd().length > 0 ? "\n\n" : "";
  return {
    changed: true,
    value: `${withoutDeprecatedFlag.trimEnd()}${suffix}[features]\nhooks = true\n`,
  };
}

export function inspectRuntimeHooks(options: RuntimeHookOptions = {}): RuntimeHookInspection {
  const paths = defaultPaths(options);
  const workContext = inspectSessionWorkContextActivation({
    path: paths.workContextPath,
    ...(options.cwd ? { cwd: options.cwd } : {}),
    ...(options.now ? { now: options.now } : {}),
  });
  const codexConfigRaw = readTextIfExists(paths.codexConfigPath);
  const codexHooksRaw = readTextIfExists(paths.codexHooksPath);
  const claudeSettingsRaw = readTextIfExists(paths.claudeSettingsPath);
  const summaryHookScriptRaw = readTextIfExists(paths.summaryHookScriptPath);
  const summaryHookScriptStatus: "current" | "stale" | "missing" = summaryHookScriptRaw === null
    ? "missing"
    : summaryHookScriptRaw === buildSessionSummaryHookScriptContent()
      ? "current"
      : "stale";
  const summaryHookCaptureCapable = Boolean(
    summaryHookScriptRaw?.includes("orgx-session-collector-state/v2") &&
      summaryHookScriptRaw.includes("enqueueSummaryCapture"),
  );
  const summaryHookFirstTurnContextCapable = Boolean(
    summaryHookScriptRaw?.includes(SESSION_SUMMARY_FIRST_TURN_CONTEXT_MARKER),
  );
  const claudeCodeSummaryHooksInstalled = hasNativeSummaryHooks(
    claudeSettingsRaw,
    "claude-code",
  );
  const codexSummaryHooksInstalled = hasNativeSummaryHooks(codexHooksRaw, "codex");
  const hasAutomaticDelivery = (raw: string | null) =>
    Boolean(
      raw &&
        raw.includes("--delivery_node=") &&
        raw.includes("--delivery_cli=") &&
        raw.includes(paths.deliveryNodePath) &&
        raw.includes(paths.deliveryCliPath) &&
        existsSync(paths.deliveryNodePath) &&
        existsSync(paths.deliveryCliPath)
    );
  const installed = {
    claudeCode: hasOrgxHook(claudeSettingsRaw),
    codex: hasOrgxHook(codexHooksRaw),
    hookScript: existsSync(paths.hookScriptPath),
    emitHookScript: existsSync(paths.emitHookScriptPath),
    summaryHookScript: summaryHookScriptStatus === "current",
    summaryHookScriptStatus,
    summaryHookCaptureCapable,
    summaryHookFirstTurnContextCapable,
    claudeCodeSummaryHooksInstalled,
    codexSummaryHooksInstalled,
    automaticDelivery:
      hasAutomaticDelivery(codexHooksRaw) ||
      hasAutomaticDelivery(claudeSettingsRaw),
  };
  return {
    paths,
    continuity: inspectClientContinuity({
      ...(options.clientContinuityPaths ? { paths: options.clientContinuityPaths } : {}),
      shared: {
        automaticDelivery: installed.automaticDelivery,
        claudeCodeInstalled: installed.claudeCode,
        claudeCodeSummaryHooksInstalled,
        codexInstalled: installed.codex,
        codexSummaryHooksInstalled,
        summaryHookInstalled: installed.summaryHookCaptureCapable,
        summaryHookStatus: installed.summaryHookScriptStatus,
        summaryHookFirstTurnContextCapable:
          installed.summaryHookFirstTurnContextCapable,
        workContextReady: workContext.ready,
      },
    }),
    workContext,
    installed,
    codex: {
      configExists: Boolean(codexConfigRaw),
      hooksEnabled: codexHooksEnabled(codexConfigRaw),
      hasNotify: codexHasNotify(codexConfigRaw),
      notifyPreserved: !codexHasNotify(codexConfigRaw) || !hasOrgxHook(codexConfigRaw),
    },
    outboxEvents: countJsonlLines(paths.outboxPath),
    sessionSummaryCaptures: countSessionSummaryCaptures(paths.sessionSummaryQueueDir),
    sessionSummaryQuarantinedCaptures: countSessionSummaryQuarantinedCaptures(
      paths.sessionSummaryQueueDir,
    ),
  };
}

export function installRuntimeHooks(
  targets: readonly RuntimeHookTarget[],
  options: RuntimeHookOptions = {},
): RuntimeHookInstallResult {
  const now = options.now ?? new Date();
  const paths = defaultPaths(options);
  const backups: string[] = [];
  const changed = {
    claudeCode: false,
    codex: false,
    codexConfig: false,
    hookScript: false,
    emitHookScript: false,
    summaryHookScript: false,
  };

  mkdirSync(dirname(paths.hookScriptPath), { recursive: true, mode: 0o700 });
  const scriptContent = buildRuntimeHookScriptContent();
  if (readTextIfExists(paths.hookScriptPath) !== scriptContent) {
    const backup = backupExisting(paths.hookScriptPath, now);
    if (backup) backups.push(backup);
    writeTextFile(paths.hookScriptPath, scriptContent, { mode: 0o700 });
    changed.hookScript = true;
  }

  mkdirSync(dirname(paths.emitHookScriptPath), { recursive: true, mode: 0o700 });
  const emitScriptContent = buildExecutionGraphEmitScriptContent();
  if (readTextIfExists(paths.emitHookScriptPath) !== emitScriptContent) {
    const backup = backupExisting(paths.emitHookScriptPath, now);
    if (backup) backups.push(backup);
    writeTextFile(paths.emitHookScriptPath, emitScriptContent, { mode: 0o700 });
    changed.emitHookScript = true;
  }

  mkdirSync(dirname(paths.summaryHookScriptPath), { recursive: true, mode: 0o700 });
  const summaryScriptContent = buildSessionSummaryHookScriptContent();
  if (readTextIfExists(paths.summaryHookScriptPath) !== summaryScriptContent) {
    const backup = backupExisting(paths.summaryHookScriptPath, now);
    if (backup) backups.push(backup);
    writeTextFile(paths.summaryHookScriptPath, summaryScriptContent, { mode: 0o700 });
    changed.summaryHookScript = true;
  }
  // Per-session counter state lives beside the hooks, owner-only.
  mkdirSync(paths.sessionStateDir, { recursive: true, mode: 0o700 });
  mkdirSync(paths.sessionSummaryQueueDir, { recursive: true, mode: 0o700 });

  if (targets.includes("codex")) {
    const rawConfig = readTextIfExists(paths.codexConfigPath);
    const nextConfig = ensureCodexHooksFeature(rawConfig);
    if (nextConfig.changed) {
      const backup = backupExisting(paths.codexConfigPath, now);
      if (backup) backups.push(backup);
      writeTextFile(paths.codexConfigPath, nextConfig.value, { mode: 0o600 });
      changed.codexConfig = true;
    }

    const rawHooks = readTextIfExists(paths.codexHooksPath);
    const nextHooks = mergeCodexHooks(rawHooks, paths, options.workEpisodeCapture);
    if (nextHooks.changed) {
      const backup = backupExisting(paths.codexHooksPath, now);
      if (backup) backups.push(backup);
      writeJsonFile(paths.codexHooksPath, nextHooks.value, { mode: 0o600 });
      changed.codex = true;
    }
  }

  if (targets.includes("claude-code")) {
    const rawSettings = readTextIfExists(paths.claudeSettingsPath);
    const nextSettings = mergeClaudeHooks(
      rawSettings,
      paths,
      options.workEpisodeCapture,
    );
    if (nextSettings.changed) {
      const backup = backupExisting(paths.claudeSettingsPath, now);
      if (backup) backups.push(backup);
      writeJsonFile(paths.claudeSettingsPath, nextSettings.value, { mode: 0o600 });
      changed.claudeCode = true;
    }
  }

  return {
    ...inspectRuntimeHooks(options),
    changed,
    backups,
  };
}

export function parseRuntimeHookTargets(value?: string): RuntimeHookTarget[] {
  if (!value?.trim()) return ["codex", "claude-code"];
  const requested = value.split(",").map((item) => item.trim().toLowerCase()).filter(Boolean);
  const expanded = requested.includes("all") ? ["codex", "claude-code"] : requested;
  const normalized = expanded.map((target) => {
    if (target === "claude" || target === "claude_code") return "claude-code";
    return target;
  });
  const invalid = normalized.filter((target) => target !== "codex" && target !== "claude-code");
  if (invalid.length > 0) {
    throw new Error(`Unsupported hook target: ${invalid.join(", ")}. Use codex, claude-code, or all.`);
  }
  return [...new Set(normalized)] as RuntimeHookTarget[];
}

export function parseRuntimeHookWorkEpisodeCapture(
  value?: string,
): RuntimeHookWorkEpisodeCapture {
  const normalized = value?.trim().toLowerCase() || "bounded";
  if (normalized === "bounded" || normalized === "metadata-only") {
    return normalized;
  }
  throw new Error(
    `Unsupported work capture: ${value}. Use bounded or metadata-only.`,
  );
}

export function runtimeHookPathsForHome(home = homedir()): RuntimeHookPaths {
  const configHome = process.env.XDG_CONFIG_HOME?.trim() || join(home, ".config");
  const hookDir = join(configHome, "useorgx", "wizard", "hooks");
  return {
    claudeSettingsPath: join(home, ".claude", "settings.json"),
    codexConfigPath: join(home, ".codex", "config.toml"),
    codexHooksPath: join(home, ".codex", "hooks.json"),
    hookScriptPath: join(hookDir, HOOK_MARKER),
    emitHookScriptPath: join(hookDir, EMIT_HOOK_MARKER),
    summaryHookScriptPath: join(hookDir, SUMMARY_HOOK_MARKER),
    sessionStateDir: join(configHome, "useorgx", "wizard", "sessions"),
    sessionSummaryQueueDir: join(configHome, "useorgx", "wizard", "session-summary-captures"),
    deliveryNodePath: process.execPath,
    deliveryCliPath: currentPackagedCliPath(),
    outboxPath: join(hookDir, "events.jsonl"),
    workContextPath: sessionWorkContextActivationPath(
      process.cwd(),
      join(configHome, "useorgx", "wizard", "session-work-contexts"),
    ),
  };
}
